"use client";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import AlertDialog from "./AlertDialog";
import { FaTrash } from "react-icons/fa";
import * as pdfjsLib from "pdfjs-dist";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import ImageModule from "docxtemplater-image-module-free";
// Set the workerSrc for pdfjsLib
pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.js";
const API = "https://erp-xe9u.onrender.com/api";

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#374151",
    borderColor: state.isFocused ? "#f97316" : "#4b5563",
    color: "#e5e7eb",
    boxShadow: "none",
    outline: "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "32px",
    height: "32px",
    fontSize: "12px",
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    outline: "none",
    boxShadow: "none",
    border: "none",
    margin: 0,
    padding: 0,
    "&:focus": {
      outline: "none",
      boxShadow: "none",
      border: "none",
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    fontSize: "12px",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
    fontSize: "12px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#4b5563" },
    fontSize: "12px",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    borderRadius: "0.375rem",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 4px",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    padding: "0",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: "2px",
  }),
};
export default function BillsTab({ project, setProject }) {
  const [pendingBills, setPendingBills] = useState([]);
  const [processedBills, setProcessedBills] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [materialOrders, setMaterialOrders] = useState([]);
  const [selectedBill, setSelectedBill] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [newAttachments, setNewAttachments] = useState([]);
  const [activeTab, setActiveTab] = useState("pending");
  const [materialOrderView, setMaterialOrderView] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [showMaterialOrderModal, setShowMaterialOrderModal] = useState(false);
  const [selectedBillForMO, setSelectedBillForMO] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [billMatchStatuses, setBillMatchStatuses] = useState({});
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [dialog, setDialog] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
    onConfirm: () => {},
  });

  const handleBillRowClick = (bill, event) => {
    // Prevent row click if the target is within the actions column
    if (event.target.closest(".actions-column")) {
      return;
    }
    handleViewBill(bill);
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No authentication token found.");
        const [
          pendingBillsResponse,
          processedBillsResponse,
          vendorsResponse,
          subcontractorsResponse,
          materialOrdersResponse,
          companyProfileResponse,
        ] = await Promise.all([
          axios.get(`${API}/bills/project/${project._id}`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { processingStatus: "pending" },
          }),
          axios.get(`${API}/bills/project/${project._id}`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { processingStatus: "processed" },
          }),
          axios.get(`${API}/accounts/vendors`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API}/users/subcontractors`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API}/materialOrders/project/${project._id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API}/company-profile`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        const pendingBillsData = pendingBillsResponse.data;
        const processedBillsData = processedBillsResponse.data;
        setPendingBills(pendingBillsData);
        setProcessedBills(processedBillsData);
        setVendors(vendorsResponse.data);
        setSubcontractors(subcontractorsResponse.data);
        setMaterialOrders(materialOrdersResponse.data);
        setCompanyProfile(companyProfileResponse.data);
        // Compute match statuses for all bills
        const allBills = [...pendingBillsData, ...processedBillsData];
        await recomputeBillMatchStatuses(allBills, token);
      } catch (err) {
        setDialog({
          open: true,
          type: "error",
          title: "Error",
          message: "Failed to load data: " + (err.message || "Unknown error"),
          onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [project._id]);
  useEffect(() => {
    if (showMaterialOrderModal && pdfBlob && canvasRef.current) {
      const renderPDF = async () => {
        try {
          const arrayBuffer = await pdfBlob.arrayBuffer();
          const pdf = await pdfjsLib.getDocument(arrayBuffer).promise;
          const page = await pdf.getPage(1);
          const viewport = page.getViewport({ scale: 1.5 });
          const canvas = canvasRef.current;
          const context = canvas.getContext("2d");
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          await page.render({ canvasContext: context, viewport }).promise;
        } catch (err) {
          setDialog({
            open: true,
            type: "error",
            title: "Error",
            message: `Failed to render PDF: ${err.message}`,
            onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
          });
        }
      };
      renderPDF();
    }
  }, [showMaterialOrderModal, pdfBlob]);

  const checkBillMaterialOrderMatch = async (bill, token) => {
    if (bill.relatedWO) return "N/A";
    if (!bill.relatedMO) return "N/A";

    try {
      const materialOrderId =
        typeof bill.relatedMO === "object" && bill.relatedMO
          ? bill.relatedMO._id
          : bill.relatedMO;
      if (!materialOrderId) return "Unknown";

      let materialOrder = materialOrders.find(
        (mo) => mo._id === materialOrderId,
      );
      if (!materialOrder) {
        const resp = await axios.get(
          `${API}/materialOrders/${materialOrderId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        materialOrder = resp.data;
        setMaterialOrders((prev) => {
          if (!prev.some((m) => m._id === materialOrder._id)) {
            return [...prev, materialOrder];
          }
          return prev;
        });
      }

      // === EXACT MO TOTAL CALCULATION ===
      const itemsSubtotal = materialOrder.items.reduce((sum, item) => {
        return sum + (item.quantity || 0) * (parseFloat(item.price) || 0);
      }, 0);
      const surchargesTotal = (materialOrder.surcharges || []).reduce(
        (sum, s) => sum + (parseFloat(s.price) || 0),
        0,
      );
      const subtotal = itemsSubtotal + surchargesTotal;
      const taxRate = parseFloat(companyProfile?.defaultTax?.taxRate) || 0;
      const hst = (subtotal * (taxRate / 100)).toFixed(2);
      const moTotal = parseFloat((subtotal + parseFloat(hst)).toFixed(2));

      const billTotal = parseFloat(bill.total) || 0;

      return isWithinTolerance(billTotal, moTotal, 0.1) ? "Check" : "Warning";
    } catch (err) {
      console.error("MO match error:", err);
      return "Unknown";
    }
  };

  const recomputeBillMatchStatuses = async (allBills, token) => {
    const matchStatuses = {};
    for (const bill of allBills) {
      matchStatuses[bill._id] = await checkBillMaterialOrderMatch(bill, token);
    }
    setBillMatchStatuses(matchStatuses);
  };
  const handleViewBill = (bill) => {
    const taxRate = companyProfile?.defaultTax?.taxRate || 0;
    const subtotal = parseFloat(bill.subtotal) || 0;
    const tax = (subtotal * (taxRate / 100)).toFixed(2);
    const total = (subtotal + parseFloat(tax)).toFixed(2);
    setSelectedBill({
      ...bill,
      invoiceDate: bill.invoiceDate ? bill.invoiceDate.split("T")[0] : "",
      datePaid: bill.datePaid ? bill.datePaid.split("T")[0] : "",
      payTo: bill.payTo?._id || bill.payTo || "",
      subContractor: bill.subContractor?._id || bill.subContractor || "",
      relatedPO: bill.relatedPO?._id || bill.relatedPO || "",
      relatedWO: bill.relatedWO?._id || bill.relatedWO || "",
      relatedMO: bill.relatedMO?._id || bill.relatedMO || "",
      project: bill.project?._id || bill.project || "",
      subtotal,
      tax: parseFloat(tax),
      total: parseFloat(total),
    });
    setNewAttachments([]);
  };
  const handleUpdateBill = async (e) => {
    e.preventDefault();
    if (!selectedBill) return;
    const token = localStorage.getItem("token");
    if (!token) {
      setDialog({
        open: true,
        type: "error",
        title: "Authentication Error",
        message: "Please log in to update the bill.",
        onConfirm: () => {
          setDialog((prev) => ({ ...prev, open: false }));
          navigate("/login");
        },
      });
      return;
    }
    const isWorkOrder = !!selectedBill.relatedWO;
    if (
      !selectedBill.billName ||
      !selectedBill.invoiceNumber ||
      !selectedBill.invoiceDate ||
      (!isWorkOrder && !selectedBill.payTo) ||
      !selectedBill.project ||
      !selectedBill.subtotal ||
      !selectedBill.total
    ) {
      setDialog({
        open: true,
        type: "error",
        title: "Validation Error",
        message: "Please fill in all required fields.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }
    // Validate material order status if relatedMO is provided
    if (selectedBill.relatedMO) {
      try {
        const materialOrderResponse = await axios.get(
          `${API}/materialOrders/${selectedBill.relatedMO}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const materialOrder = materialOrderResponse.data;
        if (materialOrder.status !== "delivered") {
          setDialog({
            open: true,
            type: "error",
            title: "Validation Error",
            message:
              "The selected material order must have a status of 'delivered' to be mapped to a bill.",
            onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
          });
          return;
        }
      } catch (err) {
        setDialog({
          open: true,
          type: "error",
          title: "Error",
          message:
            "Failed to validate material order: " +
            (err.response?.data?.message || err.message),
          onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        });
        return;
      }
    }
    if (
      selectedBill.processingStatus === "processed" &&
      !isWorkOrder &&
      selectedBill.relatedPO &&
      !selectedBill.relatedMO
    ) {
      setDialog({
        open: true,
        type: "error",
        title: "Validation Error",
        message:
          "A bill with a purchase order must be mapped to a material order when processed.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }
    try {
      const formData = new FormData();
      formData.append("billName", selectedBill.billName);
      formData.append("invoiceDate", selectedBill.invoiceDate);
      formData.append("datePaid", selectedBill.datePaid || "");
      formData.append("invoiceStatus", selectedBill.invoiceStatus);
      formData.append("invoiceNumber", selectedBill.invoiceNumber);
      formData.append("relatedPO", selectedBill.relatedPO || "");
      formData.append("relatedWO", selectedBill.relatedWO || "");
      formData.append("relatedMO", selectedBill.relatedMO || "");
      if (isWorkOrder && selectedBill.subContractor) {
        formData.append("subContractor", selectedBill.subContractor);
      } else if (!isWorkOrder) {
        formData.append("payTo", selectedBill.payTo || "");
      }
      formData.append("subtotal", selectedBill.subtotal || 0);
      formData.append("tax", selectedBill.tax || 0);
      formData.append("total", selectedBill.total || 0);
      formData.append("project", selectedBill.project || "");
      formData.append("items", JSON.stringify(selectedBill.items || []));
      formData.append(
        "attachments",
        JSON.stringify(selectedBill.attachments || []),
      );
      formData.append("processingStatus", selectedBill.processingStatus);
      newAttachments.forEach((att) => formData.append("attachments", att.file));
      const response = await axios.put(
        `${API}/bills/${selectedBill._id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );
      let updatedPendingBills = pendingBills;
      let updatedProcessedBills = processedBills;
      if (response.data.processingStatus === "pending") {
        updatedPendingBills = pendingBills.map((bill) =>
          bill._id === response.data._id ? response.data : bill,
        );
        setPendingBills(updatedPendingBills);
      } else {
        updatedProcessedBills = processedBills.map((bill) =>
          bill._id === response.data._id ? response.data : bill,
        );
        updatedPendingBills = pendingBills.filter(
          (bill) => bill._id !== response.data._id,
        );
        setProcessedBills(updatedProcessedBills);
        setPendingBills(updatedPendingBills);
      }
      await recomputeBillMatchStatuses(
        [...updatedPendingBills, ...updatedProcessedBills],
        token,
      );
      setSelectedBill(null);
      setNewAttachments([]);
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Bill updated successfully!",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to update bill: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    }
  };

  const isWithinTolerance = (a, b, percent = 0.1) => {
    const numA = parseFloat(a) || 0;
    const numB = parseFloat(b) || 0;
    const larger = Math.max(numA, numB);
    const smaller = Math.min(numA, numB);
    if (larger === 0) return true;
    const percentDiff = ((larger - smaller) / larger) * 100;

    console.log(a, b, percentDiff);
    return percentDiff <= percent;
  };

  const handleBillChange = (e) => {
    const { name, value } = e.target;
    setSelectedBill((prev) => {
      const updatedBill = { ...prev, [name]: value };
      if (name === "subtotal") {
        const subtotal = parseFloat(value) || 0;
        const taxRate = companyProfile?.defaultTax?.taxRate || 0;
        const tax = (subtotal * (taxRate / 100)).toFixed(2);
        const total = (subtotal + parseFloat(tax)).toFixed(2);
        updatedBill.subtotal = subtotal;
        updatedBill.tax = parseFloat(tax);
        updatedBill.total = parseFloat(total);
      }
      return updatedBill;
    });
  };
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = files.map((file) => ({
      name: file.name,
      url: URL.createObjectURL(file),
      file,
    }));
    setNewAttachments((prev) => [...prev, ...newFiles]);
  };

  const handleViewMaterialOrder = async (bill) => {
    const materialOrderId =
      typeof bill.relatedMO === "object" ? bill.relatedMO?._id : bill.relatedMO;
    if (!materialOrderId) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "No material order linked to this bill.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found.");

      const materialOrderResponse = await axios.get(
        `${API}/materialOrders/${materialOrderId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const materialOrder = materialOrderResponse.data;
      setSelectedBillForMO(bill);

      const response = await fetch("/material_order_template.docx");
      if (!response.ok)
        throw new Error(`Failed to load template: ${response.statusText}`);
      const templateArrayBuffer = await response.arrayBuffer();
      const zip = new PizZip(templateArrayBuffer);

      const base64Regex =
        /^(?:data:)?image\/(png|jpg|jpeg|svg|svg\+xml);base64,/;
      const validBase64 =
        /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

      function base64Parser(tagValue) {
        if (typeof tagValue !== "string" || !base64Regex.test(tagValue))
          return null;
        const stringBase64 = tagValue.replace(base64Regex, "");
        if (!validBase64.test(stringBase64)) {
          throw new Error("Error parsing base64 data");
        }
        const binaryString = window.atob(stringBase64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) bytes[i] = binaryString.charCodeAt(i);
        return bytes.buffer;
      }

      let imageBase64;
      if (
        companyProfile?.logo &&
        !companyProfile.logo.startsWith("data:image")
      ) {
        const logoResponse = await fetch(companyProfile.logo);
        if (logoResponse.ok) {
          const logoBlob = await logoResponse.blob();
          const reader = new FileReader();
          imageBase64 = await new Promise((resolve) => {
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(logoBlob);
          });
        }
      } else if (companyProfile?.logo) {
        imageBase64 = companyProfile.logo;
      }

      const imageOptions = {
        getImage: (tagValue) => base64Parser(tagValue),
        getSize: () => [125, 90],
      };

      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
        modules: [new ImageModule(imageOptions)],
      });

      const vendor = vendors.find(
        (v) => v._id === (materialOrder.vendor?._id || materialOrder.vendor),
      );
      const vendorName = vendor?.companyName || "Unknown Vendor";
      const vendorAddress = vendor?.address
        ? `${vendor.address.street}, ${vendor.address.city}, ${vendor.address.state} ${vendor.address.zip}, ${vendor.address.country}`
        : "N/A";
      const vendorContact = vendor?.contact || "N/A";

      // === SAME TOTAL CALCULATION AS TABLE & CONFIRM ===
      const itemsSubtotal = materialOrder.items.reduce((sum, item) => {
        return sum + (item.quantity || 0) * (parseFloat(item.price) || 0);
      }, 0);
      const surchargesTotal = (materialOrder.surcharges || []).reduce(
        (sum, s) => sum + (parseFloat(s.price) || 0),
        0,
      );
      const subtotal = itemsSubtotal + surchargesTotal;
      const taxRate = parseFloat(companyProfile?.defaultTax?.taxRate) || 0;
      const hst = (subtotal * (taxRate / 100)).toFixed(2);
      const moTotal = parseFloat((subtotal + parseFloat(hst)).toFixed(2));

      setMaterialOrderView({ ...materialOrder, total: moTotal });

      const items = [
        ...materialOrder.items.map((item) => ({
          materialTitle: item.projectItem?.title || "Unknown",
          qtyOrdered: item.quantity.toString(),
          price: `$${parseFloat(item.price).toFixed(2)}`,
          size: item.size || "N/A",
          container: item.projectItem?.container || "N/A",
          bidArea: item.bidArea || "(unassigned)",
        })),
        ...(materialOrder.surcharges || []).map((surcharge) => ({
          materialTitle: surcharge.description || "Surcharge",
          qtyOrdered: "N/A",
          price: `$${parseFloat(surcharge.price).toFixed(2)}`,
          container: "N/A",
          bidArea: "N/A",
        })),
      ];

      const companyName = companyProfile?.companyName || "Your Company Name";
      const companyAddress = companyProfile?.address
        ? `${companyProfile.address.street}, ${companyProfile.address.city}, ${companyProfile.address.state} ${companyProfile.address.zip}, ${companyProfile.address.country}`
        : "N/A";
      const companyPhone = companyProfile?.phone || "N/A";
      const projectName = project?.projectName || "Unknown Project";
      const projectLocation = project?.address
        ? `${project.address.street}, ${project.address.city}, ${project.address.state} ${project.address.postalCode}, ${project.address.country}`
        : project?.location || "N/A";
      const projectStartDate = project?.startDate
        ? new Date(project.startDate).toLocaleDateString()
        : "N/A";
      const expectedDeliveryDate = materialOrder?.expectedDeliveryDate
        ? new Date(materialOrder.expectedDeliveryDate).toLocaleDateString()
        : "N/A";
      const createdAt = materialOrder?.createdAt
        ? new Date(materialOrder.createdAt).toLocaleDateString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          })
        : "N/A";
      const poNumberDisplay = `${materialOrder.poNumber}`;

      const data = {
        companyName,
        companyAddress,
        companyPhone,
        projectName,
        projectLocation,
        projectStartDate,
        vendorName,
        vendorAddress,
        vendorContact,
        materialOrderNumber: materialOrder.materialOrderNumber,
        poNumber: poNumberDisplay,
        items,
        shippingMethod: materialOrder.shippingMethod,
        deliveryInstructions: materialOrder.deliveryInstructions || "N/A",
        totalCost: `$${moTotal.toFixed(2)}`,
        taxRate: `${taxRate}%`,
        taxAmount: `$${hst}`,
        subtotal: `$${subtotal.toFixed(2)}`,
        image: imageBase64,
        expectedDeliveryDate,
        createdAt,
      };

      doc.setData(data);
      doc.render();

      const docxBlob = doc.getZip().generate({
        type: "blob",
        mimeType:
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const formData = new FormData();
      formData.append(
        "docx",
        docxBlob,
        `MaterialOrder_${materialOrder.materialOrderNumber}.docx`,
      );
      formData.append("documentType", "MaterialOrder");
      formData.append("identifier", materialOrder.materialOrderNumber);

      const pdfResponse = await axios.post(
        `${API}/upload/convert-to-pdf`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          responseType: "blob",
        },
      );

      if (
        pdfResponse.status === 200 &&
        pdfResponse.headers["content-type"] === "application/pdf"
      ) {
        const pdfBlob = new Blob([pdfResponse.data], {
          type: "application/pdf",
        });
        setPdfBlob(pdfBlob);
        setPdfUrl(URL.createObjectURL(pdfBlob));
        setShowMaterialOrderModal(true);
      } else {
        const textResponse = await pdfResponse.data.text();
        let errorData;
        try {
          errorData = JSON.parse(textResponse);
        } catch {
          errorData = { message: textResponse };
        }
        throw new Error(errorData.message || "Unknown error from server");
      }
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: `Failed to generate material order PDF: ${err.message}`,
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    }
  };

  const handleConfirmBill = async (billId) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setDialog({
          open: true,
          type: "error",
          title: "Authentication Error",
          message: "Please log in to confirm the bill.",
          onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        });
        return;
      }

      const billToConfirm = pendingBills.find((bill) => bill._id === billId);
      if (!billToConfirm) {
        setDialog({
          open: true,
          type: "error",
          title: "Error",
          message: "Bill not found in pending list.",
          onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        });
        return;
      }

      const isWorkOrder = !!billToConfirm.relatedWO;
      const isPurchaseOrder = !!billToConfirm.relatedPO;

      if (!isWorkOrder && !isPurchaseOrder) {
        setDialog({
          open: true,
          type: "error",
          title: "Validation Error",
          message:
            "Bill must be mapped to either a work order or a purchase order.",
          onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        });
        return;
      }

      if (isPurchaseOrder) {
        if (!billToConfirm.relatedMO) {
          setDialog({
            open: true,
            type: "error",
            title: "Validation Error",
            message:
              "Bill cannot be confirmed. A bill with a purchase order must be mapped to a material order.",
            onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
          });
          return;
        }

        const materialOrderId =
          typeof billToConfirm.relatedMO === "object" && billToConfirm.relatedMO
            ? billToConfirm.relatedMO._id
            : billToConfirm.relatedMO;

        if (!materialOrderId) {
          setDialog({
            open: true,
            type: "error",
            title: "Validation Error",
            message: "Invalid Material Order ID.",
            onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
          });
          return;
        }

        const hasDuplicateBill = [...pendingBills, ...processedBills].some(
          (b) =>
            b._id !== billId &&
            (typeof b.relatedMO === "object" && b.relatedMO
              ? b.relatedMO._id
              : b.relatedMO) === materialOrderId,
        );
        if (hasDuplicateBill) {
          setDialog({
            open: true,
            type: "error",
            title: "Validation Error",
            message: "Another bill is already mapped to this material order.",
            onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
          });
          return;
        }

        try {
          const materialOrderResponse = await axios.get(
            `${API}/materialOrders/${materialOrderId}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          const materialOrder = materialOrderResponse.data;

          if (materialOrder.status !== "delivered") {
            setDialog({
              open: true,
              type: "error",
              title: "Validation Error",
              message: "The material order must have a status of 'delivered'.",
              onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
            });
            return;
          }

          // === EXACT SAME MO TOTAL CALCULATION AS VIEW & TABLE ===
          const itemsSubtotal = materialOrder.items.reduce((sum, item) => {
            return sum + (item.quantity || 0) * (parseFloat(item.price) || 0);
          }, 0);
          const surchargesTotal = (materialOrder.surcharges || []).reduce(
            (sum, s) => sum + (parseFloat(s.price) || 0),
            0,
          );
          const subtotal = itemsSubtotal + surchargesTotal;
          const taxRate = parseFloat(companyProfile?.defaultTax?.taxRate) || 0;
          const hst = (subtotal * (taxRate / 100)).toFixed(2);
          const moTotal = parseFloat((subtotal + parseFloat(hst)).toFixed(2));

          const billTotal = parseFloat(billToConfirm.total) || 0;

          // === YOUR EXACT TOLERANCE FORMULA (0.01%) ===
          const larger = Math.max(billTotal, moTotal);
          const smaller = Math.min(billTotal, moTotal);
          if (larger === 0) {
            setDialog({
              open: true,
              type: "error",
              title: "Validation Error",
              message: "Bill total and MO total are both zero.",
              onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
            });
            return;
          }
          const percentDiff = ((larger - smaller) / larger) * 100;

          if (percentDiff > 0.1) {
            setDialog({
              open: true,
              type: "error",
              title: "Validation Error",
              message:
                `Bill total ($${billTotal.toFixed(2)}) ` +
                `does not match calculated MO total ($${moTotal.toFixed(
                  2,
                )}). ` +
                `Difference: ${percentDiff.toFixed(
                  4,
                )}% (exceeds 0.01% tolerance).`,
              onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
            });
            return;
          }
        } catch (err) {
          setDialog({
            open: true,
            type: "error",
            title: "Error",
            message:
              "Failed to validate material order: " +
              (err.response?.data?.message || err.message),
            onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
          });
          return;
        }
      }

      const taxRate = companyProfile?.defaultTax?.taxRate || 0;
      const subtotal = parseFloat(billToConfirm.subtotal) || 0;
      const tax = (subtotal * (taxRate / 100)).toFixed(2);
      const total = (subtotal + parseFloat(tax)).toFixed(2);

      const formData = new FormData();
      formData.append("billName", billToConfirm.billName);
      formData.append(
        "invoiceDate",
        billToConfirm.invoiceDate.split("T")[0] || billToConfirm.invoiceDate,
      );
      formData.append(
        "datePaid",
        billToConfirm.datePaid ? billToConfirm.datePaid.split("T")[0] : "",
      );
      formData.append("invoiceStatus", billToConfirm.invoiceStatus);
      formData.append("invoiceNumber", billToConfirm.invoiceNumber);
      formData.append(
        "relatedPO",
        typeof billToConfirm.relatedPO === "object" && billToConfirm.relatedPO
          ? billToConfirm.relatedPO._id
          : billToConfirm.relatedPO || "",
      );
      formData.append(
        "relatedWO",
        typeof billToConfirm.relatedWO === "object" && billToConfirm.relatedWO
          ? billToConfirm.relatedWO._id
          : billToConfirm.relatedWO || "",
      );
      formData.append(
        "relatedMO",
        typeof billToConfirm.relatedMO === "object" && billToConfirm.relatedMO
          ? billToConfirm.relatedMO._id
          : billToConfirm.relatedMO || "",
      );
      if (billToConfirm.payTo)
        formData.append(
          "payTo",
          typeof billToConfirm.payTo === "object" && billToConfirm.payTo
            ? billToConfirm.payTo._id
            : billToConfirm.payTo,
        );
      if (billToConfirm.subContractor)
        formData.append(
          "subContractor",
          typeof billToConfirm.subContractor === "object" &&
            billToConfirm.subContractor
            ? billToConfirm.subContractor._id
            : billToConfirm.subContractor,
        );
      formData.append("subtotal", subtotal);
      formData.append("tax", tax);
      formData.append("total", total);
      formData.append(
        "project",
        typeof billToConfirm.project === "object" && billToConfirm.project
          ? billToConfirm.project._id
          : billToConfirm.project || "",
      );
      formData.append("items", JSON.stringify(billToConfirm.items || []));
      formData.append(
        "attachments",
        JSON.stringify(billToConfirm.attachments || []),
      );
      formData.append("processingStatus", "processed");

      const response = await axios.put(`${API}/bills/${billId}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      const confirmedBill = response.data;
      const updatedPendingBills = pendingBills.filter(
        (bill) => bill._id !== billId,
      );
      const updatedProcessedBills = [confirmedBill, ...processedBills];
      setPendingBills(updatedPendingBills);
      setProcessedBills(updatedProcessedBills);
      await recomputeBillMatchStatuses(
        [...updatedPendingBills, ...updatedProcessedBills],
        token,
      );

      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Bill confirmed and marked as processed!",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });

      if (selectedBill && selectedBill._id === billId) {
        setSelectedBill(null);
      }
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to confirm bill: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    }
  };
  const handleDeleteBill = async (billId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.delete(`${API}/bills/${billId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setPendingBills((prev) => prev.filter((bill) => bill._id !== billId));
        setBillMatchStatuses((prev) => {
          const updated = { ...prev };
          delete updated[billId];
          return updated;
        });
        setDialog({
          open: true,
          type: "success",
          title: "Success",
          message: "Bill deleted successfully!",
          onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
        });
      }
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to delete bill: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    }
  };
  const handleUploadBills = () => {
    navigate("/invoice-upload");
  };
  const vendorOptions = vendors.map((vendor) => ({
    value: vendor._id,
    label: vendor.companyName,
  }));
  const subcontractorOptions = subcontractors.map((sub) => ({
    value: sub._id,
    label: sub.displayName || sub.companyName || "Unnamed Subcontractor",
  }));
  const poOptions =
    project?.purchaseOrders?.map((po) => ({
      value: po._id,
      label: `PO: ${po.poNumber}`,
      type: "po",
    })) || [];
  const woOptions =
    project?.workOrders?.map((wo) => ({
      value: wo._id,
      label: `WO: ${wo.woNumber}`,
      type: "wo",
    })) || [];
  const moOptions = (poId) => {
    if (!poId) return [];
    return materialOrders
      .filter(
        (mo) => mo.purchaseOrder?._id === poId || mo.purchaseOrder === poId,
      )
      .map((mo) => ({
        value: mo._id,
        label: `MO: ${mo.materialOrderNumber}`,
      }));
  };
  if (isLoading)
    return <div className="text-center py-4 text-gray-200">Loading...</div>;
  const currentBills = activeTab === "pending" ? pendingBills : processedBills;
  return (
    <div className="mx-auto">
      <AlertDialog
        type={dialog.type}
        title={dialog.title}
        message={dialog.message}
        confirmText="OK"
        cancelText="Close"
        open={dialog.open}
        onClose={() => setDialog((prev) => ({ ...prev, open: false }))}
        onConfirm={dialog.onConfirm}
      />
      <div className="flex justify-between items-center mb-4 rounded-lg shadow-x min-w-full divide-y divide-gray-600 bg-gray-700 px-4 py-2">
        <div>
          <button
            onClick={() => setActiveTab("pending")}
            className={`px-4 py-2 mr-2 rounded-md font-semibold text-sm ${
              activeTab === "pending"
                ? "bg-orange-400 text-white hover:bg-orange-500"
                : "bg-gray-600 text-gray-200 hover:bg-gray-500"
            } transition-colors duration-200`}
          >
            Pending Bills
          </button>
          <button
            onClick={() => setActiveTab("processed")}
            className={`px-4 py-2 rounded-md font-semibold text-sm ${
              activeTab === "processed"
                ? "bg-orange-400 text-white hover:bg-orange-500"
                : "bg-gray-600 text-gray-200 hover:bg-gray-500"
            } transition-colors duration-200`}
          >
            Processed Bills
          </button>
        </div>
        <button
          onClick={handleUploadBills}
          className="bg-orange-400 text-white px-4 py-2 font-semibold rounded-md hover:bg-orange-500 transition-colors duration-200"
        >
          Add Bill
        </button>
      </div>
      <div className="overflow-hidden rounded-lg shadow-xl">
        <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
          <thead className="bg-gray-800">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-400 rounded-tl-lg">
                Bill Name
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-400">
                Invoice #
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-400">
                Vendor/Subcontractor
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-400">
                PO/WO #
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-400">
                MO #
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-400">
                Total Match
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-400">
                Invoice Date
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-400">
                Status
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-400">
                Sub Total
              </th>
              <th className="px-6 py-3 text-right text-sm font-semibold text-gray-400 rounded-tr-lg actions-column">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-600">
            {currentBills.length === 0 ? (
              <tr>
                <td
                  colSpan="10"
                  className="px-6 py-4 text-sm text-gray-400 text-center"
                >
                  {activeTab === "pending"
                    ? "No pending bills found."
                    : "No processed bills found."}
                </td>
              </tr>
            ) : (
              currentBills.map((bill, index) => (
                <tr
                  key={bill._id}
                  onClick={(e) => handleBillRowClick(bill, e)}
                  className="hover:bg-gray-500 cursor-pointer transition-colors duration-200"
                >
                  <td
                    className="px-6 py-4 text-sm text-gray-200"
                    title={bill.billName}
                  >
                    {bill.billName.length > 10
                      ? `${bill.billName.slice(0, 20)}...`
                      : bill.billName}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-200">
                    {bill.invoiceNumber}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-200">
                    {bill.subContractor &&
                    typeof bill.subContractor === "object"
                      ? bill.subContractor.displayName
                      : bill.payTo && typeof bill.payTo === "object"
                        ? bill.payTo.companyName
                        : bill.subContractor || bill.payTo || "N/A"}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-200">
                    {bill.relatedPO && typeof bill.relatedPO === "object"
                      ? bill.relatedPO.poNumber
                      : bill.relatedWO && typeof bill.relatedWO === "object"
                        ? bill.relatedWO.woNumber
                        : bill.relatedPO || bill.relatedWO || "N/A"}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-200">
                    {bill.relatedMO && typeof bill.relatedMO === "object"
                      ? bill.relatedMO.materialOrderNumber
                      : bill.relatedMO || "N/A"}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-200">
                    {(() => {
                      if (!bill.relatedMO || bill.relatedWO) return "N/A";

                      const moId =
                        typeof bill.relatedMO === "object"
                          ? bill.relatedMO?._id
                          : bill.relatedMO;
                      const mo = materialOrders.find((m) => m._id === moId);
                      if (!mo) return "Loading…";

                      const itemsSubtotal = (mo.items || []).reduce(
                        (s, i) =>
                          s + (i.quantity || 0) * (parseFloat(i.price) || 0),
                        0,
                      );
                      const surchargesTotal = (mo.surcharges || []).reduce(
                        (s, sc) => s + (parseFloat(sc.price) || 0),
                        0,
                      );
                      const subtotal = itemsSubtotal + surchargesTotal;
                      const taxRate =
                        parseFloat(companyProfile?.defaultTax?.taxRate) || 0;
                      const hst = subtotal * (taxRate / 100);
                      const moTotal = parseFloat((subtotal + hst).toFixed(2));
                      const billTotal = parseFloat(bill.total) || 0;

                      return isWithinTolerance(billTotal, moTotal, 0.1) ? (
                        <span className="text-green-400">✅</span>
                      ) : (
                        <span className="text-yellow-400">❗</span>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-200">
                    {new Date(bill.invoiceDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-200">
                    {bill.invoiceStatus}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-200">
                    ${bill.subtotal.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 text-sm text-right space-x-2 actions-column">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewBill(bill);
                      }}
                      className="text-orange-400 hover:text-orange-300 transition-colors duration-200"
                    >
                      View/Edit
                    </button>
                    {bill.relatedMO && !bill.relatedWO && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewMaterialOrder(bill);
                        }}
                        className="text-orange-400 hover:text-orange-300 transition-colors duration-200"
                      >
                        View MO
                      </button>
                    )}
                    {activeTab === "pending" && (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleConfirmBill(bill._id);
                          }}
                          className="text-orange-400 hover:text-orange-300 transition-colors duration-200"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteBill(bill._id);
                          }}
                          className="text-red-400 hover:text-red-300 transition-colors duration-200"
                        >
                          <FaTrash className="inline" />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {selectedBill && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-75 z-50">
          <div className="bg-gray-700 p-8 rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] overflow-y-auto border border-gray-600">
            <h3 className="text-2xl font-semibold text-white mb-6">
              {selectedBill.processingStatus === "pending"
                ? "View/Update Pending Bill"
                : "View/Edit Processed Bill"}
            </h3>
            <form onSubmit={handleUpdateBill} className="space-y-8">
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Bill Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="billName"
                      value={selectedBill.billName}
                      onChange={handleBillChange}
                      className="block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:ring-orange-500 focus:border-orange-500 text-sm py-2 px-3"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Invoice Number <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="invoiceNumber"
                      value={selectedBill.invoiceNumber}
                      onChange={handleBillChange}
                      className="block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:ring-orange-500 focus:border-orange-500 text-sm py-2 px-3"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Invoice Date <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="date"
                      name="invoiceDate"
                      value={selectedBill.invoiceDate}
                      onChange={handleBillChange}
                      className="block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:ring-orange-500 focus:border-orange-500 text-sm py-2 px-3"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Date Paid
                    </label>
                    <input
                      type="date"
                      name="datePaid"
                      value={selectedBill.datePaid}
                      onChange={handleBillChange}
                      className="block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:ring-orange-500 focus:border-orange-500 text-sm py-2 px-3"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Invoice Status <span className="text-red-400">*</span>
                    </label>
                    <select
                      name="invoiceStatus"
                      value={selectedBill.invoiceStatus}
                      onChange={handleBillChange}
                      className="block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:ring-orange-500 focus:border-orange-500 text-sm py-2 px-3"
                    >
                      <option value="unpaid">Not Paid</option>
                      <option value="paid">Paid</option>
                      <option value="partial">Partial</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Related PO/WO
                    </label>
                    <Select
                      options={[...poOptions, ...woOptions]}
                      value={
                        [...poOptions, ...woOptions].find(
                          (option) =>
                            option.value === selectedBill.relatedPO ||
                            option.value === selectedBill.relatedWO,
                        ) || null
                      }
                      onChange={(selected) =>
                        setSelectedBill((prev) => ({
                          ...prev,
                          relatedPO:
                            selected && selected.type === "po"
                              ? selected.value
                              : "",
                          relatedWO:
                            selected && selected.type === "wo"
                              ? selected.value
                              : "",
                          subContractor:
                            selected && selected.type === "wo"
                              ? prev.subContractor
                              : "",
                          relatedMO:
                            selected && selected.type === "wo"
                              ? ""
                              : prev.relatedMO,
                        }))
                      }
                      placeholder="Select a PO or WO"
                      className="text-sm"
                      styles={customStyles}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Related Material Order{" "}
                      {selectedBill.relatedPO && !selectedBill.relatedWO && (
                        <span className="text-red-400">*</span>
                      )}
                    </label>
                    <Select
                      options={moOptions(selectedBill.relatedPO)}
                      value={
                        moOptions(selectedBill.relatedPO).find(
                          (option) => option.value === selectedBill.relatedMO,
                        ) || null
                      }
                      onChange={(selected) => {
                        setSelectedBill((prev) => ({
                          ...prev,
                          relatedMO: selected ? selected.value : "",
                        }));
                      }}
                      placeholder={
                        selectedBill.relatedWO
                          ? "Optional for Work Order bills"
                          : "Select a Material Order"
                      }
                      className="text-sm"
                      styles={customStyles}
                      isDisabled={
                        !selectedBill.relatedPO || selectedBill.relatedWO
                      }
                    />
                    {selectedBill.relatedWO && (
                      <p className="text-xs text-gray-400 mt-1">
                        Material Order is optional for bills mapped to a Work
                        Order.
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      {selectedBill.relatedWO ? "Subcontractor" : "Pay To"}{" "}
                      {!selectedBill.relatedWO && (
                        <span className="text-red-400">*</span>
                      )}
                    </label>
                    {selectedBill.relatedWO ? (
                      <Select
                        options={subcontractorOptions}
                        value={
                          subcontractorOptions.find(
                            (option) =>
                              option.value === selectedBill.subContractor,
                          ) || null
                        }
                        onChange={(selected) =>
                          setSelectedBill((prev) => ({
                            ...prev,
                            subContractor: selected ? selected.value : "",
                            payTo: "",
                          }))
                        }
                        placeholder="Select a subcontractor (optional)"
                        className="text-sm"
                        styles={customStyles}
                      />
                    ) : (
                      <Select
                        options={vendorOptions}
                        value={
                          vendorOptions.find(
                            (option) => option.value === selectedBill.payTo,
                          ) || null
                        }
                        onChange={(selected) =>
                          setSelectedBill((prev) => ({
                            ...prev,
                            payTo: selected ? selected.value : "",
                            subContractor: "",
                          }))
                        }
                        placeholder="Select a vendor"
                        className="text-sm"
                        styles={customStyles}
                        required={!selectedBill.relatedWO}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                <h4 className="text-lg font-semibold text-gray-200 border-b border-gray-600 pb-2">
                  Financials
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Subtotal <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="number"
                      name="subtotal"
                      value={selectedBill.subtotal}
                      onChange={handleBillChange}
                      step="0.01"
                      className="block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:ring-orange-500 focus:border-orange-500 text-sm py-2 px-3"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Tax (Auto-calculated at{" "}
                      {companyProfile?.defaultTax?.taxRate || 0}%)
                    </label>
                    <input
                      type="number"
                      name="tax"
                      value={selectedBill.tax}
                      readOnly
                      className="block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 text-sm py-2 px-3 opacity-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Total <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="number"
                      name="total"
                      value={selectedBill.total}
                      readOnly
                      className="block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 text-sm py-2 px-3 opacity-50"
                      required
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                <h4 className="text-lg font-semibold text-gray-200 border-b border-gray-600 pb-2">
                  Attachments
                </h4>
                {(selectedBill.attachments || []).length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Existing Attachments
                    </label>
                    <div className="bg-gray-700 p-4 rounded-md border border-gray-600">
                      <ul className="list-disc list-inside text-sm text-gray-400 space-y-1">
                        {selectedBill.attachments.map((att, idx) => (
                          <li key={idx}>
                            <a
                              href={att.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-orange-400 hover:underline"
                            >
                              {att.name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
                {newAttachments.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      New Attachments
                    </label>
                    <div className="bg-gray-700 p-4 rounded-md border border-gray-600">
                      <ul className="list-disc list-inside text-sm text-gray-400 space-y-1">
                        {newAttachments.map((att, idx) => (
                          <li key={idx}>{att.name}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    Upload New Files
                  </label>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gray-600 file:text-gray-200 hover:file:bg-gray-500"
                    accept="image/*,.pdf"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-4">
                <button
                  type="button"
                  onClick={() => setSelectedBill(null)}
                  className="px-6 py-2 bg-gray-600 text-gray-200 rounded-md hover:bg-gray-500 transition-colors duration-200 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-orange-400 text-white rounded-md hover:bg-orange-500 transition-colors duration-200 text-sm font-medium"
                >
                  Update Bill
                </button>
                {selectedBill.processingStatus === "pending" && (
                  <button
                    type="button"
                    onClick={() => handleConfirmBill(selectedBill._id)}
                    className="px-6 py-2 bg-orange-400 text-white rounded-md hover:bg-orange-500 transition-colors duration-200 text-sm font-medium"
                  >
                    Confirm Bill
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
      {showMaterialOrderModal && materialOrderView && selectedBillForMO && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-75 z-50">
          <div className="bg-gray-700 p-6 rounded-lg shadow-xl max-w-4xl w-full border border-gray-600 max-h-[120vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-white">
                Material Order: {materialOrderView.materialOrderNumber}
              </h3>
              <button
                onClick={() => {
                  setShowMaterialOrderModal(false);
                  setMaterialOrderView(null);
                  setSelectedBillForMO(null);
                  setPdfBlob(null);
                  setPdfUrl(null);
                  if (pdfUrl) URL.revokeObjectURL(pdfUrl);
                }}
                className="bg-orange-400 text-white px-4 py-2 rounded-md hover:bg-orange-500 transition-colors duration-200 text-sm font-medium"
              >
                Close Preview
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-6">
              <div className="bg-gray-700 p-4 rounded-md border border-gray-600">
                <canvas
                  ref={canvasRef}
                  className="w-full rounded-md border border-gray-600"
                />
              </div>
              <div className="bg-gray-700 p-4 rounded-md border border-gray-600">
                <h4 className="text-lg font-semibold text-gray-200 mb-3">
                  Total Comparison
                </h4>
                <div className="space-y-4 text-sm text-gray-400">
                  <div>
                    <span className="font-semibold">Bill Subtotal:</span>
                    <p className="text-base font-medium text-gray-200 mt-1">
                      ${selectedBillForMO.subtotal.toFixed(2)}
                    </p>
                    <span className="font-semibold">Bill Total:</span>
                    <p className="text-base font-medium text-gray-200 mt-1">
                      ${selectedBillForMO.total.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <span className="font-semibold">Material Order Total:</span>
                    <p className="text-base font-medium text-gray-200 mt-1">
                      ${materialOrderView.total.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <span className="font-semibold">
                      Match Status (0.1% tolerance):
                    </span>
                    <p
                      className={`text-base font-medium mt-1 ${
                        isWithinTolerance(
                          parseFloat(selectedBillForMO.total || 0),
                          parseFloat(materialOrderView.total || 0),
                          0.1,
                        )
                          ? "text-green-400"
                          : "text-red-400"
                      }`}
                    >
                      {isWithinTolerance(
                        parseFloat(selectedBillForMO.total || 0),
                        parseFloat(materialOrderView.total || 0),
                        0.1,
                      )
                        ? "✅ Totals Match"
                        : "❗ Totals Do Not Match"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
